import { NgClass, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, forwardRef, inject } from '@angular/core';
import { Client } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { AdvancedCommerceListingComponentDef } from '@df/module-advanced-commerce';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { AfterContentInitDirective, AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subscription } from 'rxjs';
import { toggleTieredPriceModeListingEvent } from '../../../catalog/ui/listing/product-listing-tools.component';
import { NListing } from '../../../catalog/ui/listing/product-listing.interface';
import { ProductComponent } from '../../../catalog/ui/product/product.component';
import { GtmImpressionTrackDirective } from '../../gtm/gtm-impression-track.directive';
import { GtmTrackProductClickDirective } from '../../gtm/gtm-track-product-click.directive';
import { AdvancedCommerceListingToolsComponent } from './advanced-commerce-product-listing-tools.component';

@Component({
    selector: 'ac-product-listing',
    templateUrl: '../../../catalog/ui/listing/product-listing.html',
    standalone: true,
    imports: [
        AfterContentInitDirective,
        GridModule,
        GtmImpressionTrackDirective,
        GtmTrackProductClickDirective,
        NgClass,
        NgComponentOutlet,
        forwardRef(() => ProductComponent),
        TranslationModule
    ],
    styleUrls: ['../../../catalog/ui/listing/product-listing.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedCommerceProductListingComponent extends AdvancedCommerceListingComponentDef implements OnInit, NListing.IListing {
    readonly client = inject(Client);
    private subscriptions = new Subscription();
    canShowTieredPrice: boolean | undefined;
    protected afterContentInit = inject(AfterContentInitDirectiveService);
    private events = inject(Events);
    @Input()
    acClickCode?: string;

    @Input() searchTerm: string | undefined;
    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(
            this.afterContentInit.ready.subscribe(value => {
                if (value) {
                    this.changeDetectorRef.detectChanges();
                }
            })
        );
        this.subscriptions.add(this.events.on(toggleTieredPriceModeListingEvent).subscribe(event => this.onTieredPriceModeChange(event)));
    }

    onTieredPriceModeChange(value: boolean): void {
        this.canShowTieredPrice = value;
        this.changeDetectorRef.detectChanges();
    }

    get canShow(): boolean {
        return this.ready;
    }

    get ready(): boolean {
        return !this.store.isLoading() && !!this.store.response();
    }

    get models(): NListing.IModel[] {
        return this.store.itemsEntities() as NListing.IModel[];
    }

    get gridSize(): number {
        return this.store.listingCurrentSize();
    }

    colspan(model: any): number {
        return model.type === 'advert' ? model.size : 1;
    }

    rowspan(model: any): number {
        return 1;
    }

    get getTotal(): number {
        return this.store.metaCount();
    }

    get hasMore(): boolean {
        return this.store.hasMore();
    }

    get visibleQty(): number {
        return this.store.total();
    }

    increaseLimit(): void {
        this.store.loadMore();
    }

    get toolsOutlet(): NListing.IOutlet {
        return { component: AdvancedCommerceListingToolsComponent };
    }

    get context(): string {
        return this.element();
    }

    get clickCode(): string | undefined {
        return this.acClickCode || this.store.metaClickCode();
    }

    // need to make compilator happy (listing is needed for klevu tracking fo usual listing)
    get listing() {
        return undefined;
    }
}
